import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle, ImagePlus, Loader2, Phone, X } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { submitPartsLead, type PartsLeadInput } from "@/lib/partsLead.functions";
import { useServerFn } from "@tanstack/react-start";
import EmailField from "@/components/parts/EmailField";
import { isValidEmail, suggestEmailFix } from "@/lib/emailValidation";

interface PartsLeadFormProps {
  productId?: string | number;
  partNumber?: string | null;
  partName?: string;
  partSku?: string | null;
  price?: number | null;
  pageUrl?: string;
  pageTitle?: string;
  title?: string;
  className?: string;
  defaultTab?: "availability" | "quote" | "information";
  allowAttachment?: boolean;
}

function getUtmParams() {
  if (typeof window === "undefined") return { utm_source: "", utm_medium: "", utm_campaign: "" };
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
  };
}

function getSessionId() {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem("rex-session-id") || "";
  } catch {
    return "";
  }
}

export default function PartsLeadForm({
  productId,
  partNumber,
  partName,
  partSku,
  price,
  pageUrl,
  pageTitle,
  title,
  className = "",
  defaultTab = "availability",
  allowAttachment = false,
}: PartsLeadFormProps) {
  const [tab, setTab] = useState(defaultTab);
  const [emailTouched, setEmailTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [attachment, setAttachment] = useState<{ name: string; type: string; size: number; dataUrl: string } | null>(null);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_zip: "",
    rv_year: "",
    rv_make: "",
    rv_model: "",
    rv_vin: "",
    quantity: "1",
    install_wanted: false,
    message: "",
    consent_text: false,
    honeypot: "",
  });

  const submit = useServerFn(submitPartsLead);

  const chooseAttachment = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Choose an image", description: "Please upload a JPG, PNG, HEIC, or WebP photo.", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Photo is too large", description: "Please choose a photo smaller than 5 MB.", variant: "destructive" });
      return;
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error("Could not read photo"));
      reader.readAsDataURL(file);
    });
    setAttachment({ name: file.name, type: file.type, size: file.size, dataUrl });
  };

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (form.honeypot) return;

    const email = form.customer_email.trim();
    const phone = form.customer_phone.trim();
    if (!email && !phone) {
      toast({ title: "Contact required", description: "Please provide an email or phone number.", variant: "destructive" });
      return;
    }
    if (email && !isValidEmail(email)) {
      setEmailTouched(true);
      const fix = suggestEmailFix(email);
      toast({
        title: "Check your email address",
        description: fix ? `Did you mean ${fix}?` : "Please enter a valid email address (like name@example.com).",
        variant: "destructive",
      });
      return;
    }

    if (tab === "information" && !form.message.trim()) {
      toast({ title: "Question required", description: "Please enter your question.", variant: "destructive" });
      return;
    }

    if (phone && !form.consent_text) {
      toast({ title: "Consent required", description: "Please agree to receive text messages.", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const utm = getUtmParams();
      const payload: PartsLeadInput = {
        request_type: tab,
        customer_name: form.customer_name.trim(),
        customer_email: email || undefined,
        customer_phone: phone || undefined,
        customer_zip: form.customer_zip.trim() || undefined,
        rv_year: form.rv_year.trim() || undefined,
        rv_make: form.rv_make.trim() || undefined,
        rv_model: form.rv_model.trim() || undefined,
        rv_vin: form.rv_vin.trim() || undefined,
        part_number: partNumber || undefined,
        part_name: partName || undefined,
        part_sku: partSku || undefined,
        quantity: parseInt(form.quantity || "1", 10),
        install_wanted: tab === "quote" ? form.install_wanted : undefined,
        message: form.message.trim() || undefined,
        page_url: pageUrl || (typeof window !== "undefined" ? window.location.href : ""),
        page_title: pageTitle || undefined,
        referrer: typeof document !== "undefined" ? document.referrer : "",
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        session_id: getSessionId(),
        honeypot: form.honeypot,
        consent_text: form.consent_text,
        attachment: attachment ?? undefined,
      };

      const result = await submit({ data: payload });
      if (result.success) {
        setSuccess(true);
      } else {
        toast({ title: "Could not submit", description: result.error || "Please try again.", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Network error", description: err?.message || "Could not reach the server.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={`rounded-xl border border-border bg-card p-6 text-center ${className}`}>
        <CheckCircle className="mx-auto h-10 w-10 text-green-500" />
        <h3 className="mt-3 font-heading text-lg font-bold">Request Received</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Our parts team will review this and reach out. Need it faster? Call us at{" "}
          <a href="tel:910-679-6522" className="font-semibold text-primary hover:underline">910-679-6522</a>.
        </p>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-border bg-card p-5 shadow-sm ${className}`}>
      <h3 className="font-heading text-lg font-bold mb-1">{title || "Get Help With This Part"}</h3>
      <p className="text-sm text-muted-foreground mb-4">
        {partName ? partName : "Ask our parts team about availability, pricing, or fitment."}
      </p>

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)} className="w-full">
        <TabsList className="grid h-auto w-full grid-cols-3 gap-1 border border-border bg-muted/80 p-1 mb-4">
          <TabsTrigger
            value="availability"
            className="py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-foreground/70 data-[state=inactive]:hover:bg-secondary data-[state=inactive]:hover:text-secondary-foreground"
          >
            Availability
          </TabsTrigger>
          <TabsTrigger
            value="quote"
            className="py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-foreground/70 data-[state=inactive]:hover:bg-secondary data-[state=inactive]:hover:text-secondary-foreground"
          >
            Quote
          </TabsTrigger>
          <TabsTrigger
            value="information"
            className="py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=inactive]:text-foreground/70 data-[state=inactive]:hover:bg-secondary data-[state=inactive]:hover:text-secondary-foreground"
          >
            Info
          </TabsTrigger>
        </TabsList>

        <form onSubmit={handleSubmit} className="space-y-4">
          <TabsContent value="availability" className="mt-0 space-y-4">
            <p className="text-sm text-muted-foreground">Check stock and get a quick answer on this part.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="plf-availability-qty">Quantity</Label>
                <Input id="plf-availability-qty" type="number" min={1} max={999} value={form.quantity} onChange={set("quantity")} />
              </div>
              <div>
                <Label htmlFor="plf-availability-zip">Ship to ZIP</Label>
                <Input id="plf-availability-zip" value={form.customer_zip} onChange={set("customer_zip")} maxLength={10} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="quote" className="mt-0 space-y-4">
            <p className="text-sm text-muted-foreground">Request a quote including installation if you need it.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="plf-quote-qty">Quantity</Label>
                <Input id="plf-quote-qty" type="number" min={1} max={999} value={form.quantity} onChange={set("quantity")} />
              </div>
              <div className="flex items-end">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="plf-quote-install"
                    checked={form.install_wanted}
                    onCheckedChange={(c) => setForm((f) => ({ ...f, install_wanted: c === true }))}
                  />
                  <Label htmlFor="plf-quote-install" className="text-sm font-normal">Installation needed</Label>
                </div>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label htmlFor="plf-quote-year">RV Year</Label>
                <Input id="plf-quote-year" value={form.rv_year} onChange={set("rv_year")} maxLength={4} placeholder="2023" />
              </div>
              <div>
                <Label htmlFor="plf-quote-make">RV Make</Label>
                <Input id="plf-quote-make" value={form.rv_make} onChange={set("rv_make")} placeholder="e.g. Jayco" />
              </div>
              <div>
                <Label htmlFor="plf-quote-model">RV Model</Label>
                <Input id="plf-quote-model" value={form.rv_model} onChange={set("rv_model")} placeholder="e.g. Eagle" />
              </div>
            </div>
            <div>
              <Label htmlFor="plf-quote-vin">RV VIN (optional)</Label>
              <Input id="plf-quote-vin" value={form.rv_vin} onChange={set("rv_vin")} maxLength={50} placeholder="17-character VIN" />
            </div>
          </TabsContent>

          <TabsContent value="information" className="mt-0 space-y-4">
            <p className="text-sm text-muted-foreground">Ask a question about fitment, specs, or compatibility.</p>
            <div>
              <Label htmlFor="plf-info-message">Your Question *</Label>
              <Textarea
                id="plf-info-message"
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Does this fit a 2019 Jayco Redhawk 31XL?"
                maxLength={4000}
              />
            </div>
          </TabsContent>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="plf-name">Name</Label>
              <Input id="plf-name" value={form.customer_name} onChange={set("customer_name")} placeholder="Your name" />
            </div>
            <div>
              <Label htmlFor="plf-email">Email</Label>
              <EmailField
                id="plf-email"
                value={form.customer_email}
                onChange={(v) => setForm((f) => ({ ...f, customer_email: v }))}
                onBlur={() => setEmailTouched(true)}
                showError={emailTouched}
              />
            </div>
          </div>

          {allowAttachment && (
            <div className="space-y-2">
              <Label htmlFor="plf-attachment">Photo of the part or RV area</Label>
              {attachment ? (
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-md border border-border bg-muted p-3">
                  <div className="min-w-0 text-sm"><p className="truncate font-semibold">{attachment.name}</p><p className="text-xs text-muted-foreground">{Math.ceil(attachment.size / 1024)} KB</p></div>
                  <Button type="button" variant="ghost" size="icon" onClick={() => setAttachment(null)} aria-label="Remove photo"><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <label htmlFor="plf-attachment" className="flex min-h-20 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border bg-muted/40 px-4 text-sm font-semibold text-foreground hover:border-primary">
                  <ImagePlus className="h-5 w-5 text-primary" />Add a helpful photo
                </label>
              )}
              <input id="plf-attachment" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="sr-only" onChange={(event) => { void chooseAttachment(event.target.files?.[0]); event.target.value = ""; }} />
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="plf-phone">Phone</Label>
              <Input id="plf-phone" type="tel" value={form.customer_phone} onChange={set("customer_phone")} placeholder="(910) 555-1234" />
            </div>
            <div className="hidden" aria-hidden="true">
              <Label htmlFor="plf-website">Website</Label>
              <Input id="plf-website" value={form.honeypot} onChange={set("honeypot")} tabIndex={-1} autoComplete="off" />
            </div>
          </div>

          {form.customer_phone.trim() && (
            <div className="flex items-start space-x-2">
              <Checkbox
                id="plf-consent"
                checked={form.consent_text}
                onCheckedChange={(c) => setForm((f) => ({ ...f, consent_text: c === true }))}
              />
              <Label htmlFor="plf-consent" className="text-xs font-normal leading-tight text-muted-foreground">
                I agree to receive text messages from Rex & Sons RVs about this parts request. Message and data rates may apply. Reply STOP to opt out.
              </Label>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Phone className="mr-2 h-4 w-4" />}
            {loading ? "Sending…" : "Send to Parts Team"}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            Typical response time: same business day.
          </p>
        </form>
      </Tabs>
    </div>
  );
}
